import { Funnel, Plus, X, Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useGetMyProductFilterOptions } from '~/api/my-products';
import { Input, Select } from '~/components/ui';
import type { MyProductFilterOptionsQuery } from '~/graphql/graphql';

type Filter = {
  field: string;
  operator: string;
  value: string | number;
};
type ProductsFilterProps = {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onFiltersChange: (filters: Filter[]) => void;
};

type StringOperatorOptions = 'contains' | 'eq' | 'neq' | 'notContain';
type NumberOperatorOptions = 'eq' | 'neq' | 'gt' | 'lt' | 'gte' | 'lte';
type EnumOperatorOptions = 'eq' | 'neq';

type Operator<T> = {
  label: string;
  operator: T;
};

type FilterFieldOption<
  TOperation =
    | StringOperatorOptions
    | NumberOperatorOptions
    | EnumOperatorOptions,
> = {
  field: string;
  label: string;
  operatorOptions: Operator<TOperation>[];
  inputType: 'string' | 'number' | 'select';
  selectOptions?: { label: string; value: string }[];
};

type T = TFunction<'my-product'>;

const getStringOperators = (t: T): Operator<StringOperatorOptions>[] => [
  { label: t('filter.operators.contains'), operator: 'contains' },
  { label: t('filter.operators.is'), operator: 'eq' },
  { label: t('filter.operators.is_not'), operator: 'neq' },
  { label: t('filter.operators.does_not_contain'), operator: 'notContain' },
];

const getNumberOperators = (t: T): Operator<NumberOperatorOptions>[] => [
  { label: t('filter.operators.is'), operator: 'eq' },
  { label: t('filter.operators.is_not'), operator: 'neq' },
  { label: t('filter.operators.greater_than'), operator: 'gt' },
  { label: t('filter.operators.less_than'), operator: 'lt' },
  { label: t('filter.operators.gte'), operator: 'gte' },
  { label: t('filter.operators.lte'), operator: 'lte' },
];

const getEnumOperators = (t: T): Operator<EnumOperatorOptions>[] => [
  { label: t('filter.operators.is'), operator: 'eq' },
  { label: t('filter.operators.is_not'), operator: 'neq' },
];

const getVisibilityOptions = (t: T) => [
  {
    label: t('filter.visibility.visible_to_buyers'),
    value: 'VISIBLE_TO_BUYERS',
  },
  {
    label: t('filter.visibility.visible_to_my_buyers'),
    value: 'VISIBLE_TO_MY_BUYERS',
  },
  { label: t('filter.visibility.inactive'), value: 'INACTIVE' },
];

const getStatusOptions = (t: T) => [
  {
    label: t('filter.status.healthy'),
    value: 'HEALTHY',
  },
  {
    label: t('filter.status.low'),
    value: 'LOW',
  },
  { label: t('filter.status.critical'), value: 'CRITICAL' },
];

const getFilterOptions = (
  data: MyProductFilterOptionsQuery | undefined,
  t: T
): FilterFieldOption[] => [
  {
    field: 'visibility',
    label: t('filter.fields.visibility'),
    operatorOptions: getEnumOperators(t),
    inputType: 'select',
    selectOptions: getVisibilityOptions(t),
  },
  {
    field: 'productCode',
    label: t('filter.fields.product_code'),
    operatorOptions: getStringOperators(t),
    inputType: 'string',
  },
  {
    field: 'productName',
    label: t('filter.fields.product_name'),
    operatorOptions: getStringOperators(t),
    inputType: 'string',
  },
  {
    field: 'length',
    label: t('filter.fields.length'),
    operatorOptions: getStringOperators(t),
    inputType: 'string',
  },
  {
    field: 'pricePerUnit',
    label: t('filter.fields.price'),
    operatorOptions: getNumberOperators(t),
    inputType: 'number',
  },
  {
    field: 'stock',
    label: t('filter.fields.stock'),
    operatorOptions: getNumberOperators(t),
    inputType: 'number',
  },
  {
    field: 'status',
    label: t('filter.fields.status'),
    operatorOptions: getEnumOperators(t),
    inputType: 'select',
    selectOptions: getStatusOptions(t),
  },
  {
    field: 'replenishmentRatePerWeek',
    label: t('filter.fields.replenishment_rate'),
    operatorOptions: getNumberOperators(t),
    inputType: 'number',
  },
  {
    field: 'readyToShipDays',
    label: t('filter.fields.ready_to_ship'),
    operatorOptions: getNumberOperators(t),
    inputType: 'number',
  },

  {
    field: 'species',
    label: t('filter.fields.species'),
    operatorOptions: getEnumOperators(t),
    inputType: 'select',
    selectOptions:
      data?.lumberSpecies.map((s) => ({ label: s.name, value: s.code })) || [],
  },
  {
    field: 'grade',
    label: t('filter.fields.grade'),
    operatorOptions: getEnumOperators(t),
    inputType: 'select',
    selectOptions:
      data?.lumberGrades.map((g) => ({ label: g.name, value: g.code })) || [],
  },
  {
    field: 'dimensions',
    label: t('filter.fields.dimensions'),
    operatorOptions: getEnumOperators(t),
    inputType: 'select',
    selectOptions:
      data?.platformProductDimensions
        .filter((d) => d.nominalThicknessInches && d.nominalWidthInches)
        .map((d) => ({
          label: `${d.nominalThicknessInches} X ${d.nominalWidthInches}`,
          value: `${d.nominalThicknessInches}X${d.nominalWidthInches}`,
        })) || [],
  },
];

const DEFAULT_FILTER: Filter = {
  field: 'productCode',
  operator: 'contains',
  value: '',
};

interface FilterRowProps {
  filter: Filter;
  index: number;
  onFieldChange: (index: number, value: string) => void;
  onOperatorChange: (index: number, value: string) => void;
  onValueChange: (index: number, value: string | number) => void;
  onRemove: (index: number) => void;
  filterOptions: FilterFieldOption[];
}

function getOperatorOptionsForField(
  field: string,
  fieldOptions: FilterFieldOption[]
) {
  const option = fieldOptions.find((opt) => opt.field === field);
  const operatorOptions = option?.operatorOptions || [];
  return operatorOptions.map((op) => ({ label: op.label, value: op.operator }));
}

function getValueInputForField(
  field: string,
  fieldOptions: FilterFieldOption[],
  value: string | number,
  onChange: (value: string | number) => void,
  t: T
) {
  const option = fieldOptions.find((opt) => opt.field === field);
  if (!option) return null;

  if (option.inputType === 'select') {
    return (
      <Select
        name="value"
        options={option.selectOptions || []}
        containerClassName="flex-1 min-w-0"
        value={value}
        onChange={(_, value) => onChange(value)}
      />
    );
  }
  return (
    <Input
      name="value"
      placeholder={t('filter.row.enter_value')}
      containerClassName="flex-1 min-w-0"
      type={option.inputType}
      value={value}
      onChange={(_, value) =>
        onChange(option.inputType === 'number' ? Number(value) : value)
      }
    />
  );
}

function FilterRow({
  filter,
  index,
  onFieldChange,
  onOperatorChange,
  onValueChange,
  onRemove,
  filterOptions,
}: FilterRowProps) {
  const { t } = useTranslation('my-product');
  return (
    <div className="flex items-start gap-2">
      <div className="flex-1 space-y-2">
        <div className="text-xs font-medium text-neut-sec mb-1">
          {index === 0 ? t('filter.row.where') : t('filter.row.and')}
        </div>
        <div className="flex gap-2">
          <Select
            name={`field${index}`}
            value={filter.field}
            onChange={(_, value) => onFieldChange(index, value)}
            options={filterOptions.map((option) => ({
              label: option.label,
              value: option.field,
            }))}
            containerClassName="flex-1 min-w-0"
          />
          <Select
            name={`operator-${index}`}
            value={filter.operator}
            onChange={(_, value) => onOperatorChange(index, value)}
            options={getOperatorOptionsForField(filter.field, filterOptions)}
            containerClassName="flex-1 min-w-0"
          />
        </div>
        <div className="flex gap-2">
          {getValueInputForField(
            filter.field,
            filterOptions,
            filter.value,
            (value) => onValueChange(index, value),
            t
          )}

          <button
            className="flex-shrink-0 p-2 text-neut-sec hover:text-error-600 hover:bg-error-100 rounded-lg transition-colors"
            title={t('filter.row.remove_filter')}
            onClick={() => onRemove(index)}
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

interface FilterDropdownProps {
  filters: Filter[];
  onFieldChange: (index: number, value: string) => void;
  onOperatorChange: (index: number, value: string) => void;
  onValueChange: (index: number, value: string | number) => void;
  onRemove: (index: number) => void;
  onAdd: () => void;
  onClearAll: () => void;
  filterOptions: FilterFieldOption[];
}

function FilterDropdown({
  filters,
  onFieldChange,
  onOperatorChange,
  onValueChange,
  onRemove,
  onAdd,
  onClearAll,
  filterOptions,
}: FilterDropdownProps) {
  const { t } = useTranslation('my-product');
  return (
    <div
      data-testid="filter-dropdown"
      className="absolute top-full left-0 mt-2 w-[360px] bg-neut-bg-alt rounded-lg shadow-xl border border-neut-border z-50 overflow-hidden"
      style={{ maxHeight: '500px' }}
    >
      <div className="flex items-center justify-between p-4 border-b border-neut-border">
        <h3 className="font-semibold text-sm text-neut-prim">
          {t('filter.dropdown.title')}
        </h3>
        <button
          className="text-xs text-neut-sec hover:text-neut-prim cursor-pointer"
          onClick={onClearAll}
        >
          {t('filter.dropdown.clear_all')}
        </button>
      </div>

      <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto overflow-x-hidden">
        {filters.length === 0 ? (
          <div className="text-center py-8 text-neut-500 text-sm">
            <Funnel size={32} className="mx-auto mb-2 text-neut-300" />
            <p>{t('filter.dropdown.no_filters')}</p>
            <p className="text-xs mt-1">
              {t('filter.dropdown.no_filters_hint')}
            </p>
          </div>
        ) : (
          filters.map((filter, index) => (
            <FilterRow
              key={`${filter.field}-${filter.operator}-${index}`}
              filter={filter}
              index={index}
              onFieldChange={onFieldChange}
              onOperatorChange={onOperatorChange}
              onValueChange={onValueChange}
              onRemove={onRemove}
              filterOptions={filterOptions}
            />
          ))
        )}
      </div>
      <div className="p-3 border-t border-neut-border">
        <button
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-neut-prim hover:bg-neut-hover rounded-lg transition-colors cursor-pointer"
          onClick={onAdd}
        >
          <Plus size={14} />
          <span>{t('filter.dropdown.add_filter')}</span>
        </button>
      </div>
    </div>
  );
}

export function MyProductsFilter({
  searchTerm,
  onSearchChange,
  onFiltersChange,
}: ProductsFilterProps) {
  const { t } = useTranslation('my-product');
  const { data } = useGetMyProductFilterOptions();
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filters, setFilters] = useState<Filter[]>([]);
  const filterOptions = getFilterOptions(data, t);

  const activeFilterCount = filters.length;

  const updateFilters = (newFilters: Filter[]) => {
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleFieldChange = (index: number, value: string) => {
    const updated = [...filters];
    const filterOption = filterOptions.find((opt) => opt.field === value);
    if (!filterOption) return;

    updated[index].field = value;
    updated[index].operator = filterOption.operatorOptions[0].operator; // set the first operator
    if (
      filterOption.inputType === 'select' &&
      filterOption.selectOptions &&
      filterOption.selectOptions.length > 0
    ) {
      updated[index].value = filterOption.selectOptions[0].value; //set the first option as value
    } else {
      updated[index].value = '';
    }
    updateFilters(updated);
  };

  const handleOperatorChange = (index: number, value: string) => {
    const updated = [...filters];
    updated[index].operator = value;
    updateFilters(updated);
  };

  const handleValueChange = (index: number, value: string | number) => {
    const updated = [...filters];
    updated[index].value = value;
    updateFilters(updated);
  };

  const handleRemove = (index: number) => {
    updateFilters(filters.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    updateFilters([...filters, { ...DEFAULT_FILTER }]);
  };

  const handleClearAll = () => {
    updateFilters([]);
  };

  return (
    <div className="flex items-center gap-3 w-full justify-between sm:justify-end ">
      <div className="relative">
        <button
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium border rounded-lg transition-colors bg-prim/5 border-prim text-prim cursor-pointer"
          onClick={() => setShowFilterDropdown(!showFilterDropdown)}
        >
          <Funnel size={16} />
          <span>{t('filter.button.label')}</span>
          {activeFilterCount > 0 && (
            <span
              data-testid="filter-active-count"
              className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full bg-prim text-white"
            >
              {activeFilterCount}
            </span>
          )}
        </button>

        {showFilterDropdown && (
          <FilterDropdown
            filters={filters}
            onFieldChange={handleFieldChange}
            onOperatorChange={handleOperatorChange}
            onValueChange={handleValueChange}
            onRemove={handleRemove}
            onAdd={handleAdd}
            onClearAll={handleClearAll}
            filterOptions={filterOptions || []}
          />
        )}
      </div>

      <div className="relative min-w-[300px]">
        <Input
          name="searchTerm"
          value={searchTerm}
          onChange={(_, value) => onSearchChange(value)}
          placeholder={t('filter.search_placeholder')}
          leftIcon={<Search size={18} className="text-neut-sec" />}
          className="w-full py-2.5 text-sm font-normal"
          containerClassName="w-[80%] sm:w-full"
        />
      </div>
    </div>
  );
}
